// useViewMode — Time view URL + localStorage round-trip for the
// Calendar Month / Week segment. T-229 / time-view-week-implementation-
// plan.md §4.2 / time-view-week-spec.md §11.1.
//
// Precedence on read (mount):
//   1. URL `?view=week|month` (if present and valid).
//   2. localStorage key `time.filters.{userId}.view` (if present and valid).
//   3. Fallback `'Month'`.
//
// Writes go to BOTH URL + localStorage. Resetting to 'Month' drops the
// URL param (defaults stay implicit; mirrors the rest of the toolbar's
// short-URL preference).
//
// Unknown URL values (e.g. `?view=foo`) fall through to localStorage
// rather than throwing — the SPA should tolerate user-supplied URLs.
//
// `userId` keys the localStorage entry so a shared browser doesn't leak
// preferences across users. Tests cover this explicitly.

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CalendarViewMode } from '@pa/shared/time-view';

/** Whitelist of valid view-mode values for both URL and localStorage. */
const VIEW_MODES: readonly CalendarViewMode[] = ['Month', 'Week'] as const;

/**
 * Normalise a candidate value to the canonical `CalendarViewMode`
 * casing. Accepts case-insensitive input so URL params like
 * `?view=week` round-trip cleanly with the spec's lowercase URL
 * grammar (plan §4.2 / spec §11.1).
 */
function normaliseViewMode(
  v: string | null | undefined,
): CalendarViewMode | null {
  if (!v) return null;
  const lower = v.toLowerCase();
  for (const canon of VIEW_MODES) {
    if (canon.toLowerCase() === lower) return canon;
  }
  return null;
}

function storageKey(userId: string): string {
  return `time.filters.${userId}.view`;
}

/**
 * Hook contract per plan §4.2. Returns the resolved viewMode plus a
 * setter that persists to URL + localStorage atomically.
 */
export function useViewMode(userId: string): {
  viewMode: CalendarViewMode;
  setViewMode: (mode: CalendarViewMode) => void;
} {
  const [params, setParams] = useSearchParams();
  const urlValue = normaliseViewMode(params.get('view'));

  // Read precedence: URL → localStorage → fallback.
  const viewMode = useMemo<CalendarViewMode>(() => {
    if (urlValue) return urlValue;
    const stored =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(storageKey(userId))
        : null;
    const storedNorm = normaliseViewMode(stored);
    if (storedNorm) return storedNorm;
    return 'Month';
  }, [urlValue, userId]);

  // On first mount, if the URL contained a valid view-mode but
  // localStorage doesn't yet (or has a stale value), persist the URL
  // value forward so the next mount without `?view=` still resolves
  // to it. Per spec §11.1's read-precedence contract.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!urlValue) return;
    const stored = window.localStorage.getItem(storageKey(userId));
    if (stored !== urlValue) {
      window.localStorage.setItem(storageKey(userId), urlValue);
    }
  }, [urlValue, userId]);

  const setViewMode = useCallback(
    (mode: CalendarViewMode) => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey(userId), mode);
      }
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (mode === 'Month') {
            // Default — drop the URL param to keep URLs short.
            next.delete('view');
          } else {
            next.set('view', mode);
          }
          return next;
        },
        { replace: false },
      );
    },
    [setParams, userId],
  );

  return { viewMode, setViewMode };
}
